import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import Canvas from '../../layouts/dahboard_canvas';
import DashboardFooter from '../../layouts/dashboard_footer';
import EHRNav from '../../layouts/components/EHRNav';
import { Link } from 'react-router-dom';

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface ActivityItem {
    id: string;
    patient_name: string;
    record_name: string;
    target_clinic: string;
    status: 'pending' | 'active' | 'rejected' | 'expired';
    purpose: string;
    created_at: string;
    updated_at: string;
    action_type: 'request_created' | 'request_approved' | 'request_rejected';
}

interface SharingStats {
    total_requests: number;
    pending_requests: number;
    approved_requests: number;
    rejected_requests: number;
    active_shares: number;
}

const SharingActivity: React.FC = () => {
    const [activities, setActivities] = useState<ActivityItem[]>([]);
    const [stats, setStats] = useState<SharingStats>({ total_requests: 0, pending_requests: 0, approved_requests: 0, rejected_requests: 0, active_shares: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const base_url = useBaseUrl();

    useEffect(() => {
        document.title = "Sharing Activity - Uzima EHR";
        loadSharingData();
        const interval = setInterval(loadSharingData, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadSharingData = async () => {
        try {
            const clinic_id = await localforage.getItem("clinic_id");
            const staff_id = await localforage.getItem("staff_id");
            if (!clinic_id || !staff_id) { setError("Authentication required"); return; }

            const authHeaders = { 'Content-Type': 'application/json', 'X-Clinic-ID': clinic_id?.toString() || '', 'X-Staff-ID': staff_id?.toString() || '' };
            const auditResponse = await axios.get(`${base_url}/api/v1/sharing-audit/`, { headers: authHeaders });
            if (auditResponse.data.status === "success") {
                const data = auditResponse.data.data || [];
                setActivities(data);
                setStats({
                    total_requests: data.length,
                    pending_requests: data.filter((a: ActivityItem) => a.status === 'pending').length,
                    approved_requests: data.filter((a: ActivityItem) => a.status === 'active').length,
                    rejected_requests: data.filter((a: ActivityItem) => a.status === 'rejected').length,
                    active_shares: data.filter((a: ActivityItem) => a.status === 'active').length,
                });
            }
            setError(null);
        } catch (e: any) {
            console.error("Error loading sharing data:", e);
            setError("Failed to load sharing activity");
        } finally { setIsLoading(false); }
    };

    const STATUS_MAP: Record<string, { bg: string; color: string; label: string }> = {
        pending: { bg: 'rgba(245,158,11,0.1)', color: '#d97706', label: 'Pending' },
        active: { bg: ACCENT_SOFT, color: ACCENT, label: 'Active' },
        rejected: { bg: 'rgba(244,63,94,0.1)', color: '#e11d48', label: 'Rejected' },
        expired: { bg: 'rgba(100,116,139,0.1)', color: '#64748b', label: 'Expired' },
    };

    const ACTION_ICONS: Record<string, { icon: string; color: string }> = {
        request_created: { icon: 'bx bx-send', color: '#2563eb' },
        request_approved: { icon: 'bx bx-check-circle', color: ACCENT },
        request_rejected: { icon: 'bx bx-x-circle', color: '#e11d48' },
    };

    const statusBadge = (s: string) => {
        const c = STATUS_MAP[s] || { bg: 'rgba(100,116,139,0.1)', color: '#64748b', label: s };
        return <span style={{ padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, fontSize: '0.68rem', fontWeight: 700 }}>{c.label}</span>;
    };

    const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-history me-2" style={{ color: ACCENT }}></i>Sharing Activity</h1>
                                <p>Monitor and track medical record sharing activities</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadSharingData} disabled={isLoading}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/ehr/share" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-export"></i> Share Records</Link>
                            </div>
                        </div>

                        <EHRNav />

                        {/* Error */}
                        {error && (
                            <div style={{ padding: '12px 18px', borderRadius: 10, background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.2)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', color: '#e11d48', fontWeight: 600 }}>
                                <i className="bx bx-error-circle"></i> {error}
                            </div>
                        )}

                        {/* Stats */}
                        <div className="sc-stats" style={{ marginBottom: 20 }}>
                            {[
                                { label: 'Total Requests', val: stats.total_requests, icon: 'bx bx-file', cls: 'teal' },
                                { label: 'Pending', val: stats.pending_requests, icon: 'bx bx-time', cls: 'amber' },
                                { label: 'Approved', val: stats.approved_requests, icon: 'bx bx-check-circle', cls: 'blue' },
                                { label: 'Rejected', val: stats.rejected_requests, icon: 'bx bx-x-circle', cls: 'rose' },
                                { label: 'Active Shares', val: stats.active_shares, icon: 'bx bx-share-alt', cls: 'violet' },
                            ].map((s, i) => (
                                <div className="sc-stat" key={i}>
                                    <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                    <div className="sc-stat-value">{s.val}</div>
                                    <div className="sc-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Activity List */}
                        {isLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="sc-card" style={{ height: 72, opacity: 0.5 }}>
                                        <div className="sc-card-body" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px' }}>
                                            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#e2e8f0' }}></div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ width: '40%', height: 12, borderRadius: 6, background: '#e2e8f0', marginBottom: 6 }}></div>
                                                <div style={{ width: '60%', height: 10, borderRadius: 6, background: '#e2e8f0' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : activities.length === 0 ? (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
                                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                        <i className="bx bx-history" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                                    </div>
                                    <p style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 6 }}>No Sharing Activity</p>
                                    <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>No medical record sharing activities found.</p>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {activities.map(a => {
                                    const actionInfo = ACTION_ICONS[a.action_type] || { icon: 'bx bx-file', color: '#64748b' };
                                    return (
                                        <div key={a.id} className="sc-card" style={{ transition: 'all 0.15s' }}
                                            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; }}
                                        >
                                            <div className="sc-card-body" style={{ padding: '14px 20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                    <div style={{ width: 42, height: 42, borderRadius: 12, background: `${actionInfo.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <i className={actionInfo.icon} style={{ fontSize: '1.2rem', color: actionInfo.color }}></i>
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: 700, fontSize: '0.86rem', color: 'var(--uzima-shell-text)' }}>{a.patient_name} — {a.record_name}</div>
                                                        <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                                                            <span><i className="bx bx-building-house me-1"></i><b>To:</b> {a.target_clinic}</span>
                                                            <span><i className="bx bx-notepad me-1"></i><b>Purpose:</b> {a.purpose}</span>
                                                            <span><i className="bx bx-time me-1"></i>{fmtDate(a.updated_at)}</span>
                                                        </div>
                                                    </div>
                                                    {statusBadge(a.status)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                    </div>
                </div>
            </div>
            <Canvas />
            <DashboardFooter />
        </>
    );
};

export default SharingActivity;
